@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,500&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

:root {
  /* ── CloudIQ Studio Brand Palette ────────────────────────────── */
  /* Base Canvas: Warm editorial off-white & cream */
  --canvas-bg: #F5F2EA;
  --canvas-subtle: #EDE8DC;
  --canvas-surface: #FFFFFF;
  --canvas-sidebar: #FAF7F0;
  
  /* Ink / Dark Typography */
  --ink-900: #121826;
  --ink-800: #1E293B;
  --ink-700: #334155;
  --ink-600: #475569;
  --ink-500: #64748B;
  --ink-400: #94A3B8;
  --ink-300: #CBD5E1;

  /* Primary Royal / Cobalt Blue (Logo Arc) */
  --brand-blue: #2D5BFF;
  --brand-blue-hover: #1E40AF;
  --brand-blue-subtle: #EEF2FF;
  --brand-blue-border: #3B82F6;

  /* Secondary Data Accents (Logo Bar Chart) */
  --brand-coral: #E25B3C;        /* Bar 1 (Alerts / Low / Alarms) */
  --brand-coral-bg: #FDF2F0;
  --brand-coral-border: #EA580C;

  --brand-teal: #2F9EBB;         /* Bar 2 (Intermediate / Progress) */
  --brand-teal-bg: #E8F7FA;
  --brand-teal-border: #0891B2;

  --brand-yellow: #F3D03E;       /* Bar 3 (Warnings / Peak / Highlights) */
  --brand-yellow-bg: #FEF9E7;
  --brand-yellow-border: #D97706;

  --brand-wine: #7A1C1C;         /* Star Nodes / Critical */
  --brand-wine-bg: #F7EAEA;
  --brand-wine-border: #991B1B;

  --brand-cyan-arrow: #22D3EE;   /* Logo Upward Arrow */

  /* Structural Borders & Shadows (Neo-Brutalist & Editorial) */
  --border-ink: #1E293B;
  --border-subtle: rgba(30, 41, 59, 0.12);
  --border-card: rgba(30, 41, 59, 0.18);
  
  --shadow-solid-sm: 2px 2px 0px #1E293B;
  --shadow-solid-md: 3px 3px 0px #1E293B;
  --shadow-solid-lg: 5px 5px 0px #1E293B;
  --shadow-solid-blue: 3px 3px 0px #2D5BFF;

  /* Layout Constants */
  --sidebar-width: 270px;
  --radius-xs: 4px;
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 18px;
  --radius-xl: 24px;

  /* Typography */
  --font-serif: 'Fraunces', Georgia, serif;
  --font-sans: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;

  /* Transitions */
  --transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
  --transition-smooth: 250ms cubic-bezier(0.4, 0, 0.2, 1);
}

*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html {
  font-size: 14px;
  background-color: var(--canvas-bg);
}

body {
  font-family: var(--font-sans);
  background-color: var(--canvas-bg);
  background-image: radial-gradient(rgba(30, 41, 59, 0.05) 1px, transparent 1px);
  background-size: 24px 24px;
  color: var(--ink-900);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
}

#root {
  display: flex;
  min-height: 100vh;
}

/* ── Scrollbar ─────────────────────────────────────────────────────────── */
::-webkit-scrollbar { width: 7px; height: 7px; }
::-webkit-scrollbar-track { background: var(--canvas-bg); }
::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 10px; border: 1.5px solid var(--canvas-bg); }
::-webkit-scrollbar-thumb:hover { background: #94A3B8; }

/* ── Sidebar (Crisp Light Rail) ─────────────────────────────────────────── */
.sidebar {
  width: var(--sidebar-width);
  background: var(--canvas-sidebar);
  border-right: 1.5px solid var(--border-ink);
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 100;
  box-shadow: 2px 0 0 rgba(30, 41, 59, 0.04);
}

.sidebar-header {
  padding: 20px 18px;
  border-bottom: 1.5px solid var(--border-ink);
  background: #FFFFFF;
}

.sidebar-logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sidebar-logo-img {
  width: 46px;
  height: 46px;
  object-fit: contain;
  border-radius: var(--radius-sm);
  background: #FAF8F5;
  padding: 2px;
  border: 1.5px solid var(--border-ink);
  box-shadow: var(--shadow-solid-sm);
  flex-shrink: 0;
}

.sidebar-logo-text h1 {
  font-family: var(--font-serif);
  font-size: 15px;
  font-weight: 800;
  color: var(--brand-blue);
  letter-spacing: 0.2px;
  line-height: 1.15;
}

.sidebar-logo-text span {
  font-size: 9.5px;
  color: var(--brand-coral);
  letter-spacing: 0.1px;
  display: block;
  margin-top: 2px;
  font-weight: 600;
  font-style: italic;
}

.sidebar-nav {
  flex: 1;
  padding: 14px 12px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-section-title {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--ink-500);
  padding: 12px 10px 6px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 14px;
  border-radius: var(--radius-sm);
  color: var(--ink-700);
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: all var(--transition-fast);
}

.nav-item:hover {
  background: #FFFFFF;
  color: var(--brand-blue);
  border-color: var(--border-ink);
  box-shadow: var(--shadow-solid-sm);
}

.nav-item.active {
  background: var(--brand-blue);
  color: #FFFFFF;
  border-color: var(--border-ink);
  box-shadow: var(--shadow-solid-sm);
}

.nav-item.active svg {
  color: #FFFFFF;
}

.nav-badge {
  margin-left: auto;
  font-size: 10px;
  font-weight: 700;
  background: var(--brand-coral);
  color: #FFFFFF;
  padding: 2px 7px;
  border-radius: 12px;
  border: 1px solid var(--border-ink);
}

.sidebar-footer {
  padding: 14px 16px;
  border-top: 1.5px solid var(--border-ink);
  background: #FFFFFF;
}

.role-switcher {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  background: var(--canvas-bg);
  padding: 3px;
  border-radius: var(--radius-sm);
  border: 1.5px solid var(--border-ink);
}

.role-btn {
  background: transparent;
  border: none;
  font-size: 11px;
  font-weight: 700;
  color: var(--ink-700);
  padding: 6px 2px;
  border-radius: 4px;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.role-btn.active {
  background: var(--brand-blue);
  color: #FFFFFF;
  box-shadow: 1px 1px 0px var(--border-ink);
}

/* ── Main Layout & Topbar ──────────────────────────────────────────────── */
.main-content {
  margin-left: var(--sidebar-width);
  flex: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.topbar {
  height: 64px;
  background: #FFFFFF;
  border-bottom: 1.5px solid var(--border-ink);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  position: sticky;
  top: 0;
  z-index: 90;
}

.topbar-title {
  font-family: var(--font-serif);
  font-size: 18px;
  font-weight: 700;
  color: var(--ink-900);
  letter-spacing: -0.2px;
}

.topbar-subtitle {
  font-size: 11.5px;
  color: var(--ink-500);
  font-weight: 500;
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-content {
  flex: 1;
  padding: 28px 32px;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
}

/* ── Cards & Neo-Brutalist Containers ──────────────────────────────────── */
.card {
  background: #FFFFFF;
  border: 1.5px solid var(--border-ink);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-solid-md);
  margin-bottom: 24px;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.card:hover {
  box-shadow: 4px 4px 0px var(--border-ink);
}

.card-header {
  padding: 16px 20px;
  border-bottom: 1.5px solid var(--border-ink);
  background: var(--canvas-sidebar);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h2, .card-header h3 {
  font-family: var(--font-serif);
  font-size: 16px;
  font-weight: 700;
  color: var(--ink-900);
  margin: 0;
}

.card-body {
  padding: 20px;
}

/* ── Stat Grid & Cards ─────────────────────────────────────────────────── */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
  margin-bottom: 24px;
}

.stat-card {
  background: #FFFFFF;
  border: 1.5px solid var(--border-ink);
  border-radius: var(--radius-md);
  padding: 18px 20px;
  box-shadow: var(--shadow-solid-sm);
  position: relative;
  transition: all var(--transition-fast);
}

.stat-card:hover {
  transform: translate(-2px, -2px);
  box-shadow: var(--shadow-solid-md);
}

.stat-card-icon {
  width: 38px;
  height: 38px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  border: 1.5px solid var(--border-ink);
}

.stat-card-icon.blue { background: var(--brand-blue-subtle); color: var(--brand-blue); }
.stat-card-icon.coral, .stat-card-icon.red { background: var(--brand-coral-bg); color: var(--brand-coral); }
.stat-card-icon.teal { background: var(--brand-teal-bg); color: var(--brand-teal); }
.stat-card-icon.yellow { background: var(--brand-yellow-bg); color: #B45309; }
.stat-card-icon.purple, .stat-card-icon.wine { background: var(--brand-wine-bg); color: var(--brand-wine); }
.stat-card-icon.green { background: #ECFDF5; color: #047857; }

.stat-card-value {
  font-family: var(--font-serif);
  font-size: 26px;
  font-weight: 800;
  color: var(--ink-900);
  line-height: 1.1;
}

.stat-card-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-500);
  margin-top: 4px;
}

.stat-card-delta {
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 6px;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid var(--border-ink);
}

.stat-card-delta.up { background: #ECFDF5; color: #047857; }
.stat-card-delta.down { background: var(--brand-coral-bg); color: var(--brand-coral); }

/* ── Buttons ───────────────────────────────────────────────────────────── */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: var(--font-sans);
  font-weight: 700;
  font-size: 13.5px;
  padding: 10px 18px;
  border-radius: var(--radius-sm);
  border: 1.5px solid var(--border-ink);
  cursor: pointer;
  transition: all var(--transition-fast);
  text-decoration: none;
  box-shadow: var(--shadow-solid-sm);
}

.btn:hover {
  transform: translate(-1px, -1px);
  box-shadow: var(--shadow-solid-md);
}

.btn:active {
  transform: translate(1px, 1px);
  box-shadow: none;
}

.btn-primary {
  background: var(--brand-blue);
  color: #FFFFFF;
}

.btn-primary:hover {
  background: var(--brand-blue-hover);
}

.btn-secondary {
  background: #FFFFFF;
  color: var(--ink-900);
}

.btn-secondary:hover {
  background: var(--canvas-subtle);
}

.btn-ghost {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  color: var(--brand-blue);
}

.btn-ghost:hover {
  background: var(--brand-blue-subtle);
  border-color: var(--border-ink);
}

.btn-icon {
  width: 34px;
  height: 34px;
  border-radius: var(--radius-sm);
  border: 1.5px solid var(--border-ink);
  background: #FFFFFF;
  color: var(--ink-700);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 2px 2px 0px var(--border-ink);
  transition: all var(--transition-fast);
}

.btn-icon:hover {
  background: var(--canvas-subtle);
  color: var(--brand-blue);
  transform: translate(-1px, -1px);
}

/* ── Badges & Status Tags ──────────────────────────────────────────────── */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 6px;
  border: 1.5px solid var(--border-ink);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: 1px 1px 0px var(--border-ink);
}

.badge-ok {
  background: #E8F7FA;
  color: #0E7490;
  border-color: var(--border-ink);
}

.badge-alarm {
  background: var(--brand-coral-bg);
  color: #9A2C14;
  border-color: var(--border-ink);
}

.badge-warning {
  background: var(--brand-yellow-bg);
  color: #92400E;
  border-color: var(--border-ink);
}

.badge-info {
  background: var(--brand-blue-subtle);
  color: var(--brand-blue);
  border-color: var(--border-ink);
}

.badge-stable {
  background: var(--canvas-subtle);
  color: var(--ink-700);
  border-color: var(--border-ink);
}

.badge-wine {
  background: var(--brand-wine-bg);
  color: var(--brand-wine);
  border-color: var(--border-ink);
}

/* ── Data Tables ───────────────────────────────────────────────────────── */
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #FFFFFF;
}

.data-table th {
  background: var(--canvas-sidebar);
  color: var(--ink-700);
  font-weight: 700;
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 16px;
  border-bottom: 1.5px solid var(--border-ink);
  text-align: left;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-subtle);
  color: var(--ink-800);
  vertical-align: middle;
}

.data-table tr:hover td {
  background: #FAF8F3;
}

/* ── Forms & Controls ──────────────────────────────────────────────────── */
.form-control {
  width: 100%;
  padding: 9px 13px;
  font-family: var(--font-sans);
  font-size: 13px;
  border-radius: var(--radius-sm);
  border: 1.5px solid var(--border-ink);
  background: #FFFFFF;
  color: var(--ink-900);
  box-shadow: 2px 2px 0px rgba(30, 41, 59, 0.08);
  outline: none;
  transition: all var(--transition-fast);
}

.form-control:focus {
  border-color: var(--brand-blue);
  box-shadow: 3px 3px 0px var(--brand-blue);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}

.form-group label {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-800);
}

/* ── Modals & Dialogs ────────────────────────────────────────────────── */
.modal-overlay, .modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(18, 24, 38, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal, .modal-content {
  background: #FFFFFF;
  border: 2px solid var(--border-ink);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-solid-lg);
  width: 100%;
  max-width: 540px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal.modal-lg {
  max-width: 720px;
}

.modal-header {
  padding: 18px 24px;
  background: var(--canvas-sidebar);
  border-bottom: 1.5px solid var(--border-ink);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-header h2, .modal-header h3 {
  font-family: var(--font-serif);
  font-size: 18px;
  font-weight: 800;
  color: var(--ink-900);
  margin: 0;
}

.modal-body {
  padding: 22px 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer, .modal-actions {
  padding: 16px 24px;
  background: var(--canvas-sidebar);
  border-top: 1.5px solid var(--border-ink);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
}

/* ── Form Row Grids ────────────────────────────────────────────────────── */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 14px;
}

.form-row-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1.2fr;
  gap: 14px;
  margin-bottom: 14px;
}

@media (max-width: 640px) {
  .form-row, .form-row-3 {
    grid-template-columns: 1fr;
  }
}

.form-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-800);
  margin-bottom: 5px;
}

.form-input, .form-select {
  width: 100%;
  padding: 9px 13px;
  font-family: var(--font-sans);
  font-size: 13px;
  border-radius: var(--radius-sm);
  border: 1.5px solid var(--border-ink);
  background: var(--canvas-surface);
  color: var(--ink-900);
  box-shadow: 2px 2px 0px rgba(30, 41, 59, 0.08);
  outline: none;
  transition: all var(--transition-fast);
}

.form-input:focus, .form-select:focus {
  border-color: var(--brand-blue);
  box-shadow: 3px 3px 0px var(--brand-blue);
}

.divider {
  height: 1.5px;
  background: var(--border-subtle);
  margin: 18px 0;
}

.grid-2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 20px;
}

/* ── Helper & Layout Utilities ─────────────────────────────────────────── */
.grid-2-1 {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
}

@media (max-width: 1024px) {
  .grid-2-1 { grid-template-columns: 1fr; }
}

.flex-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.flex { display: flex; }
.gap-sm { gap: 8px; }
.mb-2 { margin-bottom: 8px; }
.mb-3 { margin-bottom: 16px; }
.mt-1 { margin-top: 4px; }
.font-mono { font-family: var(--font-mono); }
.text-xs { font-size: 11px; }
.text-sm { font-size: 12.5px; }
.text-muted { color: var(--ink-500); }
.text-accent { color: var(--brand-blue); }
.text-green { color: #047857; }

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--ink-500);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-in {
  animation: fadeIn 0.25s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ═══════════════════════════════════════════════════════════════════════════
   AUTHENTICATION SHELL — appended additions
   Everything below is additive; no rule above this line was changed.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Backfilled tokens ─────────────────────────────────────────────────────
   `--red-text`, `--navy-400` and `--accent` were referenced by App.jsx but
   never defined anywhere, so those declarations silently resolved to nothing
   (an invalid `background` fell back to transparent). They are defined here as
   aliases onto the real palette so any remaining reference renders, rather than
   leaving dangling variables in the codebase.                              */
:root {
  --red-text: var(--brand-coral);       /* #E25B3C — alerts, error copy */
  --navy-400: var(--brand-blue);        /* #2D5BFF — brand blue */
  --accent: var(--brand-teal);          /* #2F9EBB — secondary accent */
}

/* ── Screen-reader-only text ───────────────────────────────────────────────
   Visually hidden but announced, and still selectable by assistive tech.    */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ── Visible focus indicator ───────────────────────────────────────────────
   The design system relied on the UA default outline, which several rules
   suppressed. A single explicit ring keeps every control keyboard-visible.  */
:where(a, button, input, select, textarea, summary, [tabindex]):focus-visible {
  outline: 2.5px solid var(--brand-blue);
  outline-offset: 2px;
  border-radius: var(--radius-xs);
}

/* The main region takes programmatic focus from the skip link but should not
   draw a ring when clicked. */
#main-content:focus:not(:focus-visible) {
  outline: none;
}

/* ── Skip link ─────────────────────────────────────────────────────────────
   Off-screen until focused, so keyboard users can jump the sidebar.         */
.skip-link {
  position: fixed;
  top: -60px;
  left: 12px;
  z-index: 2000;
  padding: 10px 16px;
  background: var(--brand-blue);
  color: #FFFFFF;
  font-weight: 700;
  font-size: 13px;
  text-decoration: none;
  border: 1.5px solid var(--border-ink);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-solid-md);
  transition: top var(--transition-fast);
}

.skip-link:focus {
  top: 12px;
}

/* ── Login screen ──────────────────────────────────────────────────────── */
.login-shell {
  flex: 1;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
}

.login-card {
  width: 100%;
  max-width: 410px;
}

.login-brand {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 16px 22px;
  background: #FFFFFF;
  border: 2px solid var(--border-ink);
  border-bottom: none;
  border-radius: var(--radius-md) var(--radius-md) 0 0;
}

.login-logo {
  width: 46px;
  height: 46px;
  object-fit: contain;
  flex-shrink: 0;
  border: 1.5px solid var(--border-ink);
  border-radius: var(--radius-sm);
  background: #FAF8F5;
  padding: 2px;
  box-shadow: var(--shadow-solid-sm);
}

.login-wordmark {
  font-family: var(--font-serif);
  font-size: 16px;
  font-weight: 800;
  color: var(--brand-blue);
  line-height: 1.15;
}

.login-tagline {
  font-size: 10px;
  font-weight: 600;
  font-style: italic;
  color: var(--brand-coral);
  margin-top: 2px;
}

.login-body {
  background: #FFFFFF;
  border: 2px solid var(--border-ink);
  border-radius: 0 0 var(--radius-md) var(--radius-md);
  box-shadow: var(--shadow-solid-lg);
  padding: 24px 22px 26px;
}

.login-heading {
  font-family: var(--font-serif);
  font-size: 24px;
  font-weight: 800;
  color: var(--ink-900);
  letter-spacing: -0.4px;
  line-height: 1.15;
}

.login-sub {
  font-size: 12.5px;
  color: var(--ink-500);
  font-weight: 500;
  margin: 4px 0 20px;
}

.login-submit {
  width: 100%;
  margin-top: 6px;
}

.login-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: var(--shadow-solid-sm);
}

.login-footnote {
  margin-top: 14px;
  text-align: center;
  font-size: 11px;
  color: var(--ink-500);
  font-weight: 500;
}

/* ── Inline alerts ─────────────────────────────────────────────────────── */
.login-alert {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 11px 13px;
  margin-bottom: 16px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.5;
  color: #9A2C14;
  background: var(--brand-coral-bg);
  border: 1.5px solid var(--border-ink);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-solid-sm);
}

.login-alert svg {
  flex-shrink: 0;
  margin-top: 1px;
}

/* A lockout is not a typo: give it the critical treatment. */
.login-alert-lockout {
  color: var(--brand-wine);
  background: var(--brand-wine-bg);
  box-shadow: var(--shadow-solid-md);
}

.auth-notice {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 11px 13px;
  margin-bottom: 16px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.5;
  border: 1.5px solid var(--border-ink);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-solid-sm);
}

.auth-notice svg {
  flex-shrink: 0;
  margin-top: 1px;
}

.auth-notice-warn {
  background: var(--brand-yellow-bg);
  color: #92400E;
}

.auth-notice-ok {
  background: var(--brand-teal-bg);
  color: #0E7490;
  margin-bottom: 0;
}

/* ── Session restore / boot state ──────────────────────────────────────── */
.boot-shell {
  flex: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: var(--brand-blue);
}

.boot-text {
  font-family: var(--font-serif);
  font-size: 14px;
  font-weight: 700;
  color: var(--ink-700);
}

/* ── Error boundary ───────────────────────────────────────────────────── */
.boundary-shell {
  flex: 1;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
}

.boundary-card {
  width: 100%;
  max-width: 520px;
  background: #FFFFFF;
  border: 2px solid var(--border-ink);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-solid-lg);
  padding: 26px 24px;
  text-align: center;
}

.boundary-icon {
  width: 44px;
  height: 44px;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid var(--border-ink);
  border-radius: var(--radius-sm);
  background: var(--brand-coral-bg);
  color: var(--brand-coral);
  box-shadow: var(--shadow-solid-sm);
}

.boundary-title {
  font-family: var(--font-serif);
  font-size: 21px;
  font-weight: 800;
  color: var(--ink-900);
  letter-spacing: -0.3px;
}

.boundary-text {
  font-size: 13px;
  color: var(--ink-600);
  line-height: 1.6;
  margin: 8px 0 18px;
}

.boundary-details {
  margin-top: 20px;
  text-align: left;
  border-top: 1.5px solid var(--border-subtle);
  padding-top: 14px;
}

.boundary-details summary {
  font-size: 11.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--ink-500);
  cursor: pointer;
}

.boundary-pre {
  margin-top: 10px;
  padding: 11px;
  max-height: 220px;
  overflow: auto;
  font-family: var(--font-mono);
  font-size: 10.5px;
  line-height: 1.55;
  color: var(--ink-800);
  background: var(--canvas-subtle);
  border: 1.5px solid var(--border-ink);
  border-radius: var(--radius-xs);
  white-space: pre-wrap;
  word-break: break-word;
}

/* ── Sidebar: nav items are now <button> ───────────────────────────────────
   Buttons do not inherit the layout the old <div onClick> got for free.     */
button.nav-item {
  width: 100%;
  font-family: var(--font-sans);
  text-align: left;
  background: transparent;
}

/* ── Sidebar wordmark (was an <h1>, now a <p>) ─────────────────────────── */
.sidebar-wordmark {
  font-family: var(--font-serif);
  font-size: 15px;
  font-weight: 800;
  color: var(--brand-blue);
  letter-spacing: 0.2px;
  line-height: 1.15;
}

/* ── Sidebar account block ────────────────────────────────────────────── */
.account-block {
  margin-bottom: 11px;
}

.account-name {
  font-family: var(--font-serif);
  font-size: 13.5px;
  font-weight: 700;
  color: var(--ink-900);
  line-height: 1.25;
  word-break: break-word;
}

.account-email {
  font-size: 11px;
  color: var(--ink-500);
  font-weight: 500;
  word-break: break-all;
  margin-top: 1px;
}

.account-role {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 7px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #FFFFFF;
  background: var(--brand-blue);
  border: 1.5px solid var(--border-ink);
  border-radius: 6px;
  box-shadow: 1px 1px 0px var(--border-ink);
}

.account-actions {
  display: grid;
  gap: 6px;
}

.account-btn {
  width: 100%;
  font-size: 12px;
  padding: 8px 10px;
  justify-content: flex-start;
}

.account-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
}

/* ── Topbar ───────────────────────────────────────────────────────────── */
.topbar-lead {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.topbar-identity {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  font-size: 11.5px;
  font-weight: 700;
  color: #FFFFFF;
  /* Uses real palette tokens rather than the previously undefined
     --navy-400 / --accent pair. */
  background: linear-gradient(135deg, var(--brand-blue), var(--brand-teal));
  border: 1.5px solid var(--border-ink);
  border-radius: var(--radius-sm);
  box-shadow: 1px 1px 0px var(--border-ink);
}

/* ── Insufficient-permission panel ────────────────────────────────────── */
.denied-card {
  max-width: 520px;
  margin: 40px auto 0;
}

.denied-body {
  text-align: center;
  padding: 30px 26px;
}

.denied-icon {
  width: 44px;
  height: 44px;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid var(--border-ink);
  border-radius: var(--radius-sm);
  background: var(--brand-yellow-bg);
  color: #B45309;
  box-shadow: var(--shadow-solid-sm);
}

.denied-title {
  font-family: var(--font-serif);
  font-size: 19px;
  font-weight: 800;
  color: var(--ink-900);
}

.denied-text {
  font-size: 13px;
  color: var(--ink-600);
  line-height: 1.6;
  margin: 8px 0 18px;
}

/* ── Off-canvas sidebar (narrow screens) ──────────────────────────────────
   The hamburger was hard-coded to `display: none`, so the sidebar's `.open`
   class could never be applied. The toggle is hidden at desktop widths by CSS
   instead and revealed below 1024px, where the sidebar slides off-canvas.   */
.sidebar-toggle {
  display: none;
}

.sidebar-scrim {
  position: fixed;
  inset: 0;
  z-index: 95;
  border: none;
  padding: 0;
  background: rgba(18, 24, 38, 0.45);
  backdrop-filter: blur(2px);
  cursor: pointer;
  display: none;
}

@media (max-width: 1024px) {
  .sidebar-toggle {
    display: inline-flex;
  }

  .sidebar-scrim {
    display: block;
  }

  .sidebar {
    transform: translateX(-100%);
    transition: transform var(--transition-smooth);
    box-shadow: var(--shadow-solid-lg);
  }

  .sidebar.open {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0;
  }

  .topbar {
    padding: 0 18px;
  }

  .page-content {
    padding: 20px 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidebar,
  .skip-link,
  .animate-in {
    transition: none;
    animation: none;
  }
}

/* ── Compatibility aliases for dangling tokens ─────────────────────────────
   These five were referenced by inline styles in AnalyticsROI.jsx and
   CostAndSLA.jsx but never defined, so those declarations were dropped by the
   parser (a `background` of an invalid value falls back to transparent, and an
   invalid `border-color` falls back to currentColor — which is why those panels
   rendered as unstyled boxes).

   `--slate-*` are leftovers from an earlier dark theme. They are mapped onto the
   light editorial palette rather than actual dark values, because a #0F172A
   panel inside a cream layout is the bug, not the fix. Defined here so no
   reference dangles; they can be retired once those two panels use real tokens
   directly.                                                                  */
:root {
  --slate-900: var(--canvas-subtle);    /* #EDE8DC — inset panel surface */
  --slate-700: var(--border-ink);       /* #1E293B — structural rule */
  --green-text: #047857;                /* healthy / within-SLA */
  --green-border: #047857;
  --yellow-border: var(--brand-yellow-border);  /* #D97706 — warning edge */
}
